"use client";

import { createTheme, Theme } from "@mui/material/styles";
import { ListItemTextProps } from "@mui/material/ListItemText";

// Extend the ListItemText props to include our custom paddingHorizontal prop
declare module "@mui/material/ListItemText" {
  interface ListItemTextProps {
    paddingHorizontal?: number;
  }
}

const textFieldRtlOverrideCss = (theme: any) => {
  return {
    left: theme.direction === "rtl" ? "unset" : 0,
    transform:
      theme.direction === "rtl"
        ? "translate(-14px,16px) scale(1)"
        : "translate(14px,16px) scale(1)",
  };
};

export const themeConfig = {
  colorSchemes: { light: true, dark: true },
  cssVariables: {
    colorSchemeSelector: "class",
  },
  components: {
    MuiListItemText: {
      styleOverrides: {
        root: ({
          theme,
          ownerState,
        }: {
          theme: Theme;
          ownerState: ListItemTextProps;
        }) => {
          const baseStyles = {
            // Handle RTL text alignment
            textAlign:
              theme.direction === "rtl"
                ? ("right" as const)
                : ("left" as const),
            "& .MuiListItemText-primary": {
              textAlign:
                theme.direction === "rtl"
                  ? ("right" as const)
                  : ("left" as const),
            },
            "& .MuiListItemText-secondary": {
              textAlign:
                theme.direction === "rtl"
                  ? ("right" as const)
                  : ("left" as const),
            },
          };

          // Add directional padding if paddingHorizontal is specified
          if (typeof ownerState.paddingHorizontal === "number") {
            const paddingValue = theme.spacing(ownerState.paddingHorizontal);
            return {
              ...baseStyles,
              paddingLeft: theme.direction === "rtl" ? 0 : paddingValue,
              paddingRight: theme.direction === "rtl" ? paddingValue : 0,
            };
          }

          return baseStyles;
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => {
          const baseStyles = {
            textAlign:
              theme.direction === "rtl"
                ? ("right" as const)
                : ("left" as const),
          };
          return baseStyles;
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          textAlign:
            theme.direction === "rtl" ? ("right" as const) : ("left" as const),
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12, // applies to all ListItemButtons
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 24,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          // Ensure the input and label align with the theme direction
          // "& .MuiInputBase-input": textFieldRtlOverrideCss(theme),
          "& .MuiFormLabel-root": textFieldRtlOverrideCss(theme),
          '&[data-shrink="true"]': {
            // border: "1px solid red",
            color: "red",
            left: theme.direction === "rtl" ? "unset" : 0,
            transform:
              theme.direction === "rtl"
                ? "translate(-4px, -9px) scale(0.75);"
                : "translate(14px, -9px) scale(0.75);",
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '&[data-shrink="true"]': {
            left: theme.direction === "rtl" ? "unset" : 0,
            transform:
              theme.direction === "rtl"
                ? "translate(-4px, -9px) scale(0.75);"
                : "translate(14px, -9px) scale(0.75);",
          },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '&[data-shrink="true"]': {
            left: theme.direction === "rtl" ? "unset" : 0,
            transform:
              theme.direction === "rtl"
                ? "translate(-4px, -9px) scale(0.75);"
                : "translate(14px, -9px) scale(0.75);",
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
};

export const theme = createTheme(themeConfig);
